import { Box, Text, VStack } from '@chakra-ui/react';
import { useEffect, useState, useSyncExternalStore } from 'react';

import { useIsNewUser } from '#modules/user/features/onboarding/hooks/useIsNewUser';
import { useWelcomePopupStore } from '#modules/user/features/onboarding/model/welcomePopupStore';
import { useProfile } from '#modules/user/features/profile/hooks/useProfile';
import {
  trackPushSheetAction,
  trackPushSheetLater,
  trackPushSheetView,
} from '#features/analytics/utils/pushPermissionFunnel';
import {
  isUpdatePromptVisible,
  subscribeUpdatePrompt,
} from '#features/app-update/utils/statusFlags';
import { useCallStore } from '#features/calls/model/callStore';
import { i18n } from '#features/i18n/i18n';
import { usePushNotifications } from '#features/notifications/hooks/usePushNotifications';
import { Button, SectionHeading } from '#features/ui';
import { envConfig } from '#constants/envConfig';
import { useSSR } from '#hooks/useSSR';
import { useNativePushStatus } from '../../hooks/useNativePushStatus';
import { useSilentPushRegistration } from '../../hooks/useSilentPushRegistration';
import { usePushOnboardingStore } from '../../model/pushOnboardingStore';
import { openNotificationSettings } from '../../utils/deviceSettings';
import {
  markPushAttentionAsked,
  readPushAttentionAskedAt,
  reasonFromState,
  resolvePushAttention,
  type PushAttentionReason,
} from '../../utils/pushAttention';
import { isPushPromptArmed, subscribePushPrompt } from '../../utils/pushPrompt';

/**
 * Отмечает в сторе, что шторка сейчас на экране.
 *
 * Отдельным компонентом, а не эффектом в самой шторке: условия её видимости раскиданы по
 * полудюжине ранних `return null`, и единственная честная точка — факт монтирования разметки.
 * Читают флаг те, кто тоже собирается заговорить с человеком (карточка оценки Play): шторка
 * разрешений ждать не может — второго запроса система не даёт, — поэтому уступают ей.
 */
const VisibilityBeacon = ({ reason }: { reason: PushAttentionReason | null }) => {
  const setVisible = usePushOnboardingStore((state) => state.setVisible);

  useEffect(() => {
    setVisible(true);

    return () => setVisible(false);
  }, [setVisible]);

  // Показ считается здесь по той же причине, по которой здесь же выставляется флаг видимости:
  // условия показа раскиданы по полудюжине ранних `return null`, и единственная честная точка —
  // факт монтирования разметки.
  useEffect(() => {
    if (reason) {
      trackPushSheetView(reason);
    }
  }, [reason]);

  return null;
};

// Plain-string copy (native-only screen) — deliberately not via Lingui macros: these ids
// aren't in the compiled catalog (the macro would render the raw id), and the strings
// never appear on the web build. Keyed by the active locale. Framed by BENEFIT, not by the
// technical mechanism — nobody knows what a registration token is.
const COPY = {
  en: {
    notifTitle: 'Never miss a message',
    notifDesc:
      'Turn on notifications so you know when someone messages you or invites you to play.',
    allow: 'Allow',
    openSettings: 'Open settings',
    notifDeniedDesc:
      "Notifications are turned off for the app. Turn them on in settings so you don't miss messages.",
    allSetTitle: 'All set',
    allSetDesc: 'Notifications are on — messages and invites will reach this phone.',
    later: 'Later',
    done: 'Done',
  },
  ru: {
    notifTitle: 'Не пропустите сообщение',
    notifDesc: 'Включите уведомления, чтобы узнавать о сообщениях и приглашениях в игру.',
    allow: 'Включить',
    openSettings: 'Открыть настройки',
    notifDeniedDesc:
      'Уведомления для приложения выключены. Включите их в настройках, чтобы не пропускать сообщения.',
    allSetTitle: 'Всё настроено',
    allSetDesc: 'Уведомления включены – сообщения и приглашения дойдут до этого телефона.',
    later: 'Позже',
    done: 'Готово',
  },
} as const;

/**
 * Шторка «включите уведомления» для нативной оболочки.
 *
 * Раньше здесь было два яруса: разрешение ОС, а следом — батарея, автозапуск и показ звонка на
 * экране блокировки, и весь набор переспрашивался после КАЖДОГО обновления приложения.
 * Измерения 03.09.2026 на MIUI разобрали это основание по частям:
 *
 *  - MIUI действительно снимает автозапуск на обновлении (проверено сравнением `appops` до и
 *    после настоящего апдейта) — но пуш доходит и без него: убитый свайпом процесс поднимают
 *    Play Services (`Start proc … caller=com.google.android.gms`). Проверено и со снятым
 *    батарейным исключением.
 *  - Разрешение на уведомления, батарейное исключение и каналы обновление ПЕРЕЖИВАЮТ.
 *
 * То есть второй ярус просил то, что теряется на каждом обновлении и ни на что не влияет.
 * Осталось одно, от чего доставка зависит по-настоящему: разрешение и живой токен на сервере.
 * Разрешения для звонков живут отдельно — `useLockScreenCallPermission` и карточка в
 * настройках, там им и место: они нужны рингу, а не сообщениям.
 *
 * Показ решается ПРИЧИНОЙ, а не событием: см. `utils/pushAttention.ts`. Версия сборки из
 * ключей ушла — она была неверным основанием, и из-за неё в день с тремя релизами человек
 * получал одну и ту же шторку трижды.
 */
export const PushOnboarding = () => {
  const { isBrowser } = useSSR();
  const { profile } = useProfile();
  const userId = profile?.id;
  const { enableNotifications } = usePushNotifications();
  const { state, refresh } = useNativePushStatus();
  /*
   * Разрешение уже есть, а токена на сервере нет — это чинится без единого экрана, и сначала
   * пробуем именно так. Экран здесь был бы просьбой разрешить то, что уже разрешено.
   */
  const { attempted } = useSilentPushRegistration(state);
  const forceShow = usePushOnboardingStore((s) => s.forceShow);
  const hideForce = usePushOnboardingStore((s) => s.hide);
  // A ringing call owns the screen. This sheet is a one-time nicety and it was covering the
  // incoming-call card — the caller's name, the answer button, all of it — which is the worst
  // possible moment to ask someone about notification settings.
  const callPhase = useCallStore((state) => state.phase);
  // Same idea for the welcome popup: in the app a brand-new account now lands straight in
  // onboarding, and stacking the permission sheet on top of it means two modal flows in the
  // first seconds. Permissions wait — the sheet reappears on the next launch until dismissed.
  // Two signals, because the store alone loses the race: this component sits at the root and
  // unblocks as soon as `me` resolves, while the store only flips once the router has reached
  // the profile form and the popup's effect has run. The URL flag covers exactly that gap.
  const welcomePopupOpen = useWelcomePopupStore((state) => state.open);
  const isNewUser = useIsNewUser();

  /** Закрыта в этом сеансе. Живёт в памяти: постоянную отметку пишет только «Позже». */
  const [dismissed, setDismissed] = useState(false);

  const t = COPY[i18n.locale === 'ru' ? 'ru' : 'en'];

  /*
   * Экран обновления или работ занял всё окно — тогда нам здесь не место.
   *
   * Чек-лист рисуется выше модальных окон намеренно, и до этой проверки он ложился поверх
   * заглушки: человек, которому продукт только что сказал «я закрыт», получал сверху просьбу
   * выдать разрешения на уведомления. Согласия так не собирают — их так теряют.
   *
   * Подписка, а не снимок: чек-лист монтируется по своему поводу (доехало состояние
   * устройства) и вполне может успеть раньше, чем вернётся проверка версии.
   */
  const gateBusy = useSyncExternalStore(subscribeUpdatePrompt, isUpdatePromptVisible, () => false);

  /*
   * Спрашиваем не при запуске, а после первого действия, ради которого уведомления и нужны:
   * человек написал сообщение, создал заявку или вошёл в сессию — то есть ждёт ответа.
   *
   * Причина в цене ошибки. Android 13+ даёт ровно две попытки показать системный диалог, после
   * второго отказа он не появится никогда, и остаётся только дорога в настройки. Спрашивать до
   * того, как человек понял, зачем мы ему, — самый дорогой способ потратить эти две попытки.
   */
  const promptArmed = useSyncExternalStore(subscribePushPrompt, isPushPromptArmed, () => false);

  if (!envConfig.IS_NATIVE || !isBrowser || !userId || gateBusy) {
    return null;
  }

  if (callPhase !== 'idle' && callPhase !== 'ended') {
    return null;
  }

  if (welcomePopupOpen || isNewUser) {
    return null;
  }

  if (dismissed && !forceShow) {
    return null;
  }

  // Повода ещё не было — молчим. Из настроек шторку по-прежнему можно открыть руками.
  if (!promptArmed && !forceShow) {
    return null;
  }

  // Причина и откат считаются вместе: отметка о «Позже» хранится по причине, поэтому прочитать
  // её можно только зная, о чём собираемся говорить.
  const candidate = reasonFromState(state);
  const reason = candidate
    ? resolvePushAttention({
        state,
        askedAt: readPushAttentionAskedAt(candidate),
        now: Date.now(),
      })
    : null;

  // Само по себе состояние «пока не знаем» ничего не показывает: пугать до того, как прочитаны
  // разрешение и профиль, — значит показывать предупреждение, которое через секунду исчезнет.
  if (!reason && !forceShow) {
    return null;
  }

  // Про отсутствующий токен заговариваем только после тихой попытки: обычно она всё чинит сама,
  // и человек не должен узнать, что что-то было не так.
  if (reason === 'no-token' && !attempted && !forceShow) {
    return null;
  }

  /** «Позже»/«Готово» — единственная кнопка, которая молчит потом неделю или месяц. */
  const finish = () => {
    if (reason) {
      markPushAttentionAsked(reason, Date.now());
      trackPushSheetLater(reason);
    }
    setDismissed(true);
    hideForce();
  };

  // Backdrop tap = dismiss for THIS session only (don't persist): an accidental tap outside
  // shouldn't suppress the sheet for a week — it'll show again on the next launch.
  const dismissSession = () => {
    setDismissed(true);
    hideForce();
  };

  const onAllow = () => {
    if (reason) {
      trackPushSheetAction(reason);
    }
    void (async () => {
      await enableNotifications();
      // Состояние читается заново сразу: разрешение и токен меняются в этот же миг, и без
      // перечитывания шторка осталась бы висеть поверх уже включённых уведомлений.
      refresh();
    })();
  };

  const onOpenNotificationSettings = () => {
    if (reason) {
      trackPushSheetAction(reason);
    }
    void openNotificationSettings();
  };

  return (
    <Box
      position="fixed"
      inset={0}
      zIndex={2000}
      bg="blackAlpha.600"
      display="flex"
      alignItems="flex-end"
      justifyContent="center"
      onClick={dismissSession}
    >
      <VisibilityBeacon reason={reason} />
      <Box
        w="100%"
        maxW="480px"
        bg="gray.900"
        borderTopRadius="2xl"
        borderWidth="1px"
        borderBottomWidth={0}
        p={6}
        pb={8}
        onClick={(e) => e.stopPropagation()}
      >
        <VStack align="stretch" gap={5}>
          {reason ? (
            <>
              <Box>
                <SectionHeading>{t.notifTitle}</SectionHeading>
                <Text mt={2} textStyle="sm" color="gray.400">
                  {reason === 'denied' ? t.notifDeniedDesc : t.notifDesc}
                </Text>
              </Box>
              {/*
                Отказ на системном экране нельзя переспросить диалогом — на Android 13+ система
                его больше не покажет, и кнопка «Разрешить» молча ничего бы не делала. Поэтому
                при `denied` ведём в настройки, а в остальных случаях повторяем регистрацию:
                она же поднимает и системный диалог, если его ещё не показывали.
              */}
              {reason === 'denied' ? (
                <Button onClick={onOpenNotificationSettings}>{t.openSettings}</Button>
              ) : (
                <Button onClick={onAllow}>{t.allow}</Button>
              )}
              <Button variant="ghost" onClick={finish}>
                {t.later}
              </Button>
            </>
          ) : (
            <>
              <Box>
                <SectionHeading>{t.allSetTitle}</SectionHeading>
                <Text mt={2} textStyle="sm" color="gray.400">
                  {t.allSetDesc}
                </Text>
              </Box>
              <Button onClick={finish}>{t.done}</Button>
            </>
          )}
        </VStack>
      </Box>
    </Box>
  );
};
